<template>
  <span class="sp-chip" :class="classModifiers" part="base">
    <span ref="prependSlot" class="sp-chip__prepend" :class="prependModifiers" part="prepend">
      <slot name="prepend">
        <sp-icon v-if="prependIcon" :name="prependIcon" size="x-small"></sp-icon>
      </slot>
    </span>

    <span ref="labelSlot" class="sp-chip__label" part="label">
      <slot>{{ label }}</slot>
    </span>

    <sp-button
      v-if="isClosable"
      class="sp-chip__close"
      part="close"
      variant="text"
      size="xx-small"
      :data-testid="closeTestId"
      :aria-label="closeLabel"
      @click.stop="onClose"
    >
      <sp-icon name="close" size="xx-small"></sp-icon>
    </sp-button>
  </span>
</template>

<script setup>
import { computed, useTemplateRef } from "vue";

import { useBreakpoints } from "#ce/composables/useBreakpoints";
import { useColor } from "#ce/composables/useColor";
import { useSize } from "#ce/composables/useSize";
import { useVariant } from "#ce/composables/useVariant";
import { t } from "#ce/shared/components-i18n";
import { isPresent } from "#ce/utils/props";
import { hasSlotContent } from "#ce/utils/slots";
import { props as chipProps } from "./props.js";

const props = defineProps(chipProps);

const emit = defineEmits(["close"]);

// The icon renders as the slot's fallback, so one flattened read covers both sources.
const prependSlot = useTemplateRef("prependSlot");
const prependModifiers = computed(() => ({ "--has-content": hasSlotContent(prependSlot.value) }));

const isClosable = computed(() => isPresent(props.closable));

const { sizeModifiers } = useSize(props);
const { variantModifiers } = useVariant(props);
const { colorModifiers } = useColor(props);
const { breakpointModifiers } = useBreakpoints();

// Names the remove button for assistive tech, which reaches it with no surrounding text to read.
// The slot wins when it has content, because that is what the chip displays.
const labelSlot = useTemplateRef("labelSlot");
const closeLabel = computed(() =>
  t("chip_remove", { label: slottedText(labelSlot.value) || props.label || "" }).trim(),
);

function slottedText(wrapper) {
  const slot = wrapper?.querySelector("slot");

  return slot
    ?.assignedNodes({ flatten: true })
    .map((node) => node.textContent)
    .join("")
    .replace(/\s+/g, " ")
    .trim();
}

const classModifiers = computed(() => [
  ...breakpointModifiers.value,
  ...sizeModifiers.value,
  ...variantModifiers.value,
  ...colorModifiers.value,
  { "--pill": isPresent(props.pill) },
  { "--closable": isClosable.value },
]);

function onClose() {
  emit("close");
}
</script>

<style>
:host {
  display: inline-flex;
  max-width: 100%;
}
</style>

<style lang="scss" scoped>
@mixin chip-size($size) {
  // Every branch sets the whole set: the breakpoint selectors outrank the base size, so anything
  // left unset here would keep the base size's value and mix two scales.
  @if $size == x-small {
    --chip-padding-inline: var(--sp-ref-spacing-4);
    --chip-padding-block: var(--sp-ref-spacing-1);
    --chip-gap: var(--sp-ref-spacing-2);

    font-size: var(--sp-ref-font-size-small);
  } @else if $size == small {
    --chip-padding-inline: var(--sp-ref-spacing-5);
    --chip-padding-block: var(--sp-ref-spacing-2);
    --chip-gap: var(--sp-ref-spacing-3);

    font-size: var(--sp-ref-font-size-small);
  } @else if $size == medium {
    --chip-padding-inline: var(--sp-ref-spacing-6);
    --chip-padding-block: var(--sp-ref-spacing-3);
    --chip-gap: var(--sp-ref-spacing-4);

    font-size: var(--sp-ref-font-size-medium);
  } @else if $size == large {
    --chip-padding-inline: var(--sp-ref-spacing-7);
    --chip-padding-block: var(--sp-ref-spacing-4);
    --chip-gap: var(--sp-ref-spacing-4);

    font-size: var(--sp-ref-font-size-medium);
  }
}

.sp-chip {
  // `--role-*` carries whichever colour role is set; the public hooks wrap it, so a consumer
  // override outranks the role rather than being replaced by it.
  --role-surface: var(--sp-sys-color-primary-container);
  --role-fill: var(--sp-sys-color-primary);
  --role-on-fill: var(--sp-sys-color-on-primary);

  --chip-surface: var(--sp-comp-chip-background-color, var(--role-surface));
  --chip-text: var(--sp-comp-chip-color, var(--sp-sys-color-on-surface));
  --chip-fill: var(--sp-comp-chip-fill-color, var(--role-fill));
  --chip-on-fill: var(--sp-comp-chip-on-fill-color, var(--role-on-fill));
  --chip-outline: var(--sp-comp-chip-border-color, var(--chip-fill));
  --chip-foreground: var(--chip-text);
  --chip-padding-inline: var(--sp-comp-chip-padding-inline, var(--sp-ref-spacing-6));
  --chip-padding-block: var(--sp-comp-chip-padding-block, var(--sp-ref-spacing-3));
  --chip-gap: var(--sp-comp-chip-gap, var(--sp-ref-spacing-4));
  --chip-close-size: var(--sp-comp-chip-close-size, 1.25rem);

  display: inline-flex;
  box-sizing: border-box;
  align-items: center;
  gap: var(--chip-gap);
  max-width: 100%;
  padding: var(--chip-padding-block) var(--chip-padding-inline);
  border: var(--sp-comp-chip-border-width, var(--sp-ref-spacing-px)) solid transparent;
  border-radius: var(--sp-comp-chip-border-radius, var(--sp-ref-border-radius-lg));
  background-color: var(--chip-surface);
  color: var(--chip-text);
  font-size: var(--sp-comp-chip-font-size, var(--sp-ref-font-size-medium));
  font-weight: var(--sp-comp-chip-font-weight, var(--sp-ref-font-weight-medium));
  line-height: var(--sp-comp-chip-line-height, 1.25);

  &__label {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  &__prepend,
  &__close {
    flex: 0 0 auto;
    align-items: center;
  }

  // The nested button defaults to the primary role, which collides with a chip filled from that
  // same role, so its foreground follows the chip's own.
  &__close {
    display: inline-flex;

    --sp-comp-button-min-width: var(--chip-close-size);
    --sp-comp-button-width: var(--chip-close-size);
    --sp-comp-button-xx-small-height: var(--chip-close-size);
    --sp-comp-button-xx-small-padding: 0;
    --sp-comp-button-border-radius: 50%;
    --sp-comp-button-font-weight: var(--sp-ref-font-weight-normal);
    --sp-comp-button-primary-variant-text-text-color: var(--chip-foreground);
    --sp-comp-button-primary-hover-variant-text-text-color: var(--chip-foreground);
    --sp-comp-button-primary-hover-variant-text-bg-opacity: 0.12;
    --sp-comp-button-primary-hover-outlined-bg-color: var(--chip-foreground);
    --sp-comp-icon-fill: var(--chip-foreground);
    --fill: var(--chip-foreground);
  }

  &__prepend {
    display: none;

    &.--has-content {
      display: inline-flex;
    }
  }

  // A closable chip carries the button's own padding, so the trailing inset comes off.
  &.--closable {
    padding-inline-end: var(--sp-comp-chip-padding-inline-closable, var(--sp-ref-spacing-3));
  }

  &.--pill {
    border-radius: var(--sp-comp-chip-border-radius-pill, 999rem);
  }

  &.--variant-flat {
    --chip-foreground: var(--chip-on-fill);

    background-color: var(--chip-fill);
    color: var(--chip-on-fill);
  }

  &.--variant-outlined {
    --chip-foreground: var(--chip-fill);

    background-color: transparent;
    border-color: var(--chip-outline);
    color: var(--chip-fill);
  }

  &.--variant-plain {
    --chip-foreground: var(--chip-fill);

    padding-inline: 0;
    background-color: transparent;
    color: var(--chip-fill);
  }

  // A role's fill takes the platform's paired `on-` token, so a dark fill gets the light font
  // without the call site choosing one. Tonal text stays `on-surface`: `on-<role>-container`
  // resolves near-white in some themes, which is unreadable on a pale container.
  @each $color in primary, secondary, tertiary, danger, error, warning, info {
    &.--color-#{$color} {
      --role-surface: var(--sp-sys-color-#{$color}-container);
      --role-fill: var(--sp-sys-color-#{$color});
      --role-on-fill: var(--sp-sys-color-on-#{$color});
    }
  }

  // The platform gives success no container token, so tonal mixes its own role into the surface
  // it sits on. Mixing into `transparent` would leave the chip translucent over whatever is behind.
  &.--color-success {
    --role-surface: color-mix(in srgb, var(--sp-sys-color-success) 14%, var(--sp-sys-color-surface));
    --role-fill: var(--sp-sys-color-success);
    --role-on-fill: var(--sp-sys-color-on-success);
  }

  @each $size in (x-small small medium large) {
    &.--size-#{$size},
    &.--size-sm-#{$size}.--is-sm-screen,
    &.--size-md-#{$size}.--is-md-screen {
      @include chip-size($size);
    }
  }
}
</style>
